import React, { useCallback, useEffect, useState } from 'react'

import { v4 as uniqid } from 'uuid'

import { DateTime } from 'luxon'

import FormStatus from '@/Form/FormStatus'
import SubmitForm from '@/Form/V2/SubmitForm'
import Input      from '@/Form/V2/Input'

import { budget_options } from '@Config/options'

import Loader  from '@Pure/Loader'

import bemit from '@function/bemit'

import FormsStore from '@Store/Forms'

import { num, formatNum } from '@root/utility'

const FORM_NAME = 'ECRenewal';

export default function EcRenewalForm ({ EC, infos, onSuccess, ...props })
{
	const [step, setStep] = useState(1);
	const [status, setStatus] = useState({ });
	const [submit, setSubmit] = useState();

	const loading = FormsStore.useState(s => s[FORM_NAME]?._loading === true);

	const physical_cards = FormsStore.useState(s => s[FORM_NAME]?.physical_cards);
	const guides_count   = FormsStore.useState(s => s[FORM_NAME]?.guides_count);
	const people         = FormsStore.useState(s => s[FORM_NAME]?.people);

	const errors = status.fields || { };

	useEffect(function ()
	{
		if (!EC) return;

		FormsStore.update(function (store)
		{
			store[FORM_NAME] ||= { };

			store[FORM_NAME].people = String(num(props.active_accounts));
		});
	},
	[EC, props.active_accounts]);

	const nextStep = useCallback(function (e)
	{
		FormsStore.update(function (s)
		{
			if (num(s[FORM_NAME]?.people) < num(s[FORM_NAME]?.physical_cards))
			{
				setStatus(
				{
					error: 'Le nombre de cartes physiques ne peut dépasser ' +
						'le nombre d\'actifs.'
				});
			}
			else
			{
				setStatus({ });
				setStep(step => step + 1);
			}
		});
	},
	[]);

	const previousStep = useCallback(function (e)
	{
		setStep(step => (step-1 < 0 ? 0 : step - 1));
	},
	[]);

	const budget = (infos ? infos.budget_range : EC?.budget_range) || 'none';

	const budget_data = budget_options
	.find(opt => opt.value === budget) || { };

	const confirmInfos = useCallback(function (e)
	{
		setSubmit(uniqid());
	},
	[]);

	const onError = useCallback(function (error_data)
	{
		setStatus(error_data);
	},
	[]);

	const handleSuccess = useCallback(function (response)
	{
		setStatus({ info: response.info });
		onSuccess(response);
	},
	[onSuccess]);

	const loader = loading === true ?
	(
		<Loader className="c-loader" bemit={ ['white', 'bg-green'] } />
	)
	: undefined;

	const confirm_btns = step !== 1 && !status.info ?
	(
		<div className="u-full-width u-flex u-gap-30 u-items-center
			u-content-center u-flex-wrap">

			<button type="button" className={ bemit('c-btn', ['extra']) }
				onClick={ previousStep }>
				Précédent
			</button>

			<button type="button"
				className={ bemit('c-btn', ['extra', 'green']) }
				onClick={ confirmInfos }>
				Valider et envoyer
				{ loader }
			</button>

		</div>
	)
	: undefined;

	const partner_cost = num(num(EC.renewal_partner_access_cost) * num(people, 0));

	const unit_partner_price = num(
		partner_cost / (num(people) || 1)
	);

	const partner_access_cost =
	(
		<div className="c-EC-renewal__amount-price">
			Accès module partenaire&nbsp;:
			{ ' ' }
			<strong>
				{ formatNum(partner_cost,'0,0[.]00') + ' €' }
			</strong>
			{ ' (' + unit_partner_price + '€ / utilisateur / an)' }
		</div>
	);

	const prices = budget_data.value === 'none' ?
	(
		<div className="c-EC-renewal__amount">

			{
				num(EC.renewal_entry_cost) > 0 ?
				(
					<div className="c-EC-renewal__amount-price">
						Droit d'entrée&nbsp;:
						{ ' ' }
						<strong>
							{ formatNum(EC.renewal_entry_cost, '0,0[.]00') + ' €' }
						</strong>
					</div>
				)
				: undefined
			}

			<div className="c-EC-renewal__amount-price">
				Adhésion&nbsp;:
				{ ' ' }
				<strong>
					{
						formatNum(
							num(EC.renewal_unit_cost) * num(people, 0),
							'0,0[.]00'
						) + ' €'
					}
				</strong>
			</div>

			{ partner_access_cost }

		</div>
	)
	:
	(
		<div className="c-EC-renewal__amount">
			<div className="c-EC-renewal__amount-price">
				Adhésion&nbsp;:
				{ ' ' }
				<strong>
					{
						num(budget_data.amount) > 0
						? formatNum(budget_data.amount, '0,0[.]00') + ' €'
						: 'Nous contacter'
					}
				</strong>
			</div>

			{ partner_access_cost }
		</div>
	);

// à partir de cette date on est sur un renouvellement de l'année en cours
// sinon de l'année précédente
	const year_threshold = DateTime.now().startOf('year').plus({ months: 12 });

	const RENEWAL_YEAR = DateTime.now() > year_threshold
	? DateTime.now().toFormat('yyyy')
	: DateTime.now().startOf('year').minus({ days: 7 }).toFormat('yyyy');

	const physical_limit = DateTime.fromISO(RENEWAL_YEAR + '-12-22'); // 22 décembre

	const physical_disabled = DateTime.now() > physical_limit;

	const current_step = step === 1 ?
	(
		<>
			<div className="u-flex u-flex-wrap u-gap-20">

				<Input name="people"
					form={ FORM_NAME }
					bemit={ ['50%'] }
					error={ errors['people']?.error }
					label="Nombre d'actifs"
				/>

				<Input name="people"
					form={ FORM_NAME }
					bemit={ ['50%'] }
					error={ errors['people]']?.error }
					label="Nombre de cartes virtuelles"
					read-only
				/>

				<Input name="physical_cards"
					form={ FORM_NAME }
					bemit={ ['50%'] }
					error={ errors['physical_cards]']?.error }
					label="Nombre de cartes papier souhaitées"
					read-only={ physical_disabled }
				/>

				<Input name="guides_count"
					form={ FORM_NAME }
					bemit={ ['50%'] }
					error={ errors['guides_count]']?.error }
					label="Nombre de guides utilisateur papier"
					read-only={ physical_disabled }
				/>

			</div>

			<FormStatus { ...status } />

			<div className="u-full-width u-text-center">

				<button type="button" className={ bemit('c-btn', ['extra']) +
				' u-margin-top-20' }
					onClick={ nextStep }>
					Suivant
				</button>

			</div>

		</>
	)
	:
	(
		<>
			<h2 style={ {  width: 'auto', margin: 'auto' } }>
				Liste de vos élus sur la plateforme
			</h2>

			{ props.admins_table }

			<div className="EC-renewal-recap">
				<h2 className="EC-renewal-recap__title">
					Informations déclarées
				</h2>

				<div className="EC-renewal-recap__el">
					<div className="EC-renewal-recap__label">
						Nombre d'actifs&nbsp;:
					</div>
					<div className="EC-renewal-recap__value">
						{ formatNum(people, '0,0') }
					</div>
				</div>

				<div className="EC-renewal-recap__el">
					<div className="EC-renewal-recap__label">
						Nombre de cartes virtuelles&nbsp;:
					</div>
					<div className="EC-renewal-recap__value">
						{ formatNum(people, '0,0') }
					</div>
				</div>

				<div className="EC-renewal-recap__el">
					<div className="EC-renewal-recap__label">
						Nombre de cartes papier souhaitées&nbsp;:
					</div>
					<div className="EC-renewal-recap__value">
						{ formatNum(physical_cards, '0,0') }
					</div>
				</div>

				<div className="EC-renewal-recap__el">
					<div className="EC-renewal-recap__label">
						Nombre de guides utilisateur papier&nbsp;:
					</div>
					<div className="EC-renewal-recap__value">
						{ formatNum(guides_count, '0,0') }
					</div>
				</div>
			</div>

			{ prices }

			<div className="u-full-width u-text-center">

				<FormStatus { ...status } />

				{ confirm_btns }

			</div>
		</>
	);

	return (
		<form className={ bemit('c-form', ['EC-renewal']) }
			method="POST" action="/EC-renewal/save">

			<div className={ bemit('c-title', ['form-v2']) + ' u-margin-bottom-0' }>
				Informations de mise à jour
			</div>

			{ current_step }

			{
				submit ?
				(
					<SubmitForm form={ FORM_NAME } id={ submit }
						onError={ onError }
						onSuccess={ handleSuccess }
						url="/EC-renewal/save-infos"
					/>
				)
				: undefined
			}

		</form>
	);
}